<!-- No header: the close button floats over the content on its own.
     Fixed rather than scrolled away with the top of the list, because it is the only way back on
     this screen — a guest ten cards down would otherwise have to reach for browser Back. -->
<div class="ev-close-bar">
  <button type="button" class="ev-close" (click)="back()"
          [attr.aria-label]="'events.back' | translate">
    <app-event-icon name="close" [size]="18"></app-event-icon>
  </button>
</div>

<div class="ev-page">
  <!-- The screen has no visible title, matching iOS. Kept for screen readers, which would otherwise
       land on a page with no heading at all. -->
  <h1 class="ev-heading-sr">{{ 'events.title' | translate }}</h1>

  <!-- Venue | City. ABOVE every state, never inside one: it is chrome, not content, and a control
       that lives inside the error or empty branch disappears (or stops taking taps) on exactly the
       venue this feature exists for — one with no programme of its own. That is I-23, which cost
       iOS a device session. A venue with no city never renders this at all. -->
  <app-events-segment-control *ngIf="showsCitySegment"
    [segment]="segment"
    (segmentChange)="selectSegment($event)">
  </app-events-segment-control>

  <!-- ===================== Venue ===================== -->
  <ng-container *ngIf="segment === 'venue'">
    <!-- Total failure. A venue that simply has no events falls through to the empty state instead. -->
    <div class="ev-state" *ngIf="error">
      <p class="ev-state-text">{{ error }}</p>
      <button type="button" class="ev-primary-btn" (click)="retry()">
        {{ 'events.retry' | translate }}
      </button>
    </div>

    <ng-container *ngIf="!error">
      <!-- Featured strip -->
      <section class="ev-section" *ngIf="hasFeatured" [attr.aria-busy]="loading">
        <h2 class="ev-section-title" *ngIf="!loading">{{ 'events.featured' | translate }}</h2>
        <div class="ev-section-title-skeleton" *ngIf="loading"></div>

        <div class="ev-carousel">
          <ng-container *ngIf="loading">
            <app-event-skeleton class="ev-carousel-item" variant="featured"
              *ngFor="let s of skeletonCards"></app-event-skeleton>
          </ng-container>

          <app-event-card class="ev-carousel-item" *ngFor="let event of featured; trackBy: trackById"
            [event]="event"
            (openEvent)="onOpen($event, 'featured')"
            (addToCalendar)="onAddToCalendar($event)">
          </app-event-card>
        </div>
      </section>

      <!-- All events -->
      <section class="ev-section" *ngIf="hasAll" [attr.aria-busy]="loading">
        <h2 class="ev-section-title" *ngIf="!loading">{{ 'events.all' | translate }}</h2>
        <div class="ev-section-title-skeleton" *ngIf="loading"></div>

        <div class="ev-list">
          <ng-container *ngIf="loading">
            <app-event-skeleton *ngFor="let s of skeletonCards" variant="list"></app-event-skeleton>
          </ng-container>

          <app-event-vertical-card *ngFor="let event of all; trackBy: trackById"
            [event]="event"
            [showBusinessInfo]="false"
            (openEvent)="onOpen($event, 'all')"
            (addToCalendar)="onAddToCalendar($event)">
          </app-event-vertical-card>

          <app-event-skeleton *ngIf="loadingMore" variant="list"></app-event-skeleton>
        </div>
      </section>

      <!-- Nothing scheduled -->
      <div class="ev-state" *ngIf="isEmpty">
        <img class="ev-state-image" src="assets/no-results.png" alt="" />
        <h2 class="ev-state-title">{{ 'events.emptyTitle' | translate }}</h2>
        <p class="ev-state-text">{{ 'events.emptyBody' | translate }}</p>
        <button type="button" class="ev-primary-btn" (click)="retry()">
          {{ 'events.retry' | translate }}
        </button>
      </div>
    </ng-container>
  </ng-container>

  <!-- ===================== City =====================
       The SAME two sections and the SAME cards, fed from our API instead of Firestore. Nothing
       below is a city-specific card: a city event is an EventDoc like any other, which is the whole
       point of mapping it onto the existing model. -->
  <ng-container *ngIf="segment === 'city'">
    <!-- Both sections dead, with nothing to show for it. One section failing while the other
         returns is a partial result, and the half that worked is better than a blank screen. -->
    <div class="ev-state" *ngIf="showsCityError">
      <p class="ev-state-text">{{ 'events.error' | translate }}</p>
      <button type="button" class="ev-primary-btn" (click)="retryCity()">
        {{ 'events.retry' | translate }}
      </button>
    </div>

    <ng-container *ngIf="!showsCityError">
      <!-- Featured strip -->
      <section class="ev-section" *ngIf="hasCityFeatured" [attr.aria-busy]="cityLoading">
        <h2 class="ev-section-title" *ngIf="!cityLoading">{{ 'events.featured' | translate }}</h2>
        <div class="ev-section-title-skeleton" *ngIf="cityLoading"></div>

        <div class="ev-carousel" (scroll)="onCityCarouselScroll($event)">
          <ng-container *ngIf="cityLoading">
            <app-event-skeleton class="ev-carousel-item" variant="featured"
              *ngFor="let s of skeletonCards"></app-event-skeleton>
          </ng-container>

          <app-event-card class="ev-carousel-item"
            *ngFor="let event of cityFeatured.events; trackBy: trackById"
            [event]="event"
            (openEvent)="onOpen($event, 'city_featured')"
            (addToCalendar)="onAddToCalendar($event)">
          </app-event-card>

          <app-event-skeleton class="ev-carousel-item" variant="featured"
            *ngIf="cityFeatured.loadingMore"></app-event-skeleton>
        </div>
      </section>

      <!-- All events -->
      <section class="ev-section" *ngIf="hasCityAll" [attr.aria-busy]="cityLoading">
        <h2 class="ev-section-title" *ngIf="!cityLoading">{{ 'events.all' | translate }}</h2>
        <div class="ev-section-title-skeleton" *ngIf="cityLoading"></div>

        <div class="ev-list">
          <ng-container *ngIf="cityLoading">
            <app-event-skeleton *ngFor="let s of skeletonCards" variant="list"></app-event-skeleton>
          </ng-container>

          <app-event-vertical-card *ngFor="let event of cityAll.events; trackBy: trackById"
            [event]="event"
            [showBusinessInfo]="false"
            (openEvent)="onOpen($event, 'city_all')"
            (addToCalendar)="onAddToCalendar($event)">
          </app-event-vertical-card>

          <app-event-skeleton *ngIf="cityAll.loadingMore" variant="list"></app-event-skeleton>
        </div>
      </section>

      <!-- Reachable only in the moment between a retry that comes back empty and the segment
           removing itself, since an empty city hides the control entirely. -->
      <div class="ev-state" *ngIf="isCityEmpty">
        <img class="ev-state-image" src="assets/no-results.png" alt="" />
        <h2 class="ev-state-title">{{ 'events.emptyTitle' | translate }}</h2>
        <p class="ev-state-text">{{ 'events.emptyBody' | translate }}</p>
      </div>
    </ng-container>
  </ng-container>
</div>

<div class="ev-toast" *ngIf="toast" role="status">{{ toast }}</div>
